// src/models/Insights/workZoneBreachUserResolver.ts
//
// Resolves a Work Zone Breach's opaque `authId` to a display name and a group name, for the
// User and Group columns on WorkZoneAdaptCard (PRD 2026-09-14-work-zone-breach-user-
// attribution-prd.md, amended D9/AR5 2026-09-15).
//
// The original implementation used two sources. The plan's originally-specified design (D9, tasks
// 4.3/4.4) called for a single resolver built from `OrganizationProvider`'s `groups` state -
// the sorted `Group[]` that `loadGroups` builds from per-group member-payload fetches. An
// investigation on 2026-09-15 found that state is INCOMPLETE for at least one real org
// (`SAFEGDEV`): the live API returns 14 groups / 63 auth_ids, but `groups` holds only 13
// groups / 40 members - the `Default` group is ENTIRELY ABSENT from that state, and
// `Default` holds 20 of the org's 21 ops users, i.e. the actual device wearers who generate
// Work Zone Breaches. Building the resolver over `groups` alone would therefore silently
// fail to name precisely the population this feature exists to name.
//
// This module does NOT fix that underlying `groups`-state bug (separate, later ticket).
// It now accepts the already-loaded group-member payload as a fallback, while retaining the
// two original sources and never triggering another GetGroupMembers fan-out:
//
//  - NAME  <- `getAllOrgCompassDevices()` (`@/repository/OrgCompassDevicesDAO`). Device-
//    scoped data, so `Default`-group members ARE covered - they still own or last used a
//    device; they are only missing from the provider's *derived per-group membership*
//    state, not from the device fleet. `buildBreachDeviceNameMap` below turns its response
//    into `Map<last_auth_id, last_user_display_name>`.
//  - NAME FALLBACK <- the injected `groups[].group_value` member payload. This is only used
//    when the device list has no usable display name.
//  - GROUP <- `getUsersGroup(organization, authId)` (`OrganizationProvider`). Reads the RAW
//    `organization.groups` document directly - a different structure from the derived
//    `groups` state - and is therefore complete (all 63 users) and unaffected by the
//    `Default`-group gap.
//  - GROUP FALLBACK <- the same loaded group-member payload, when the raw organization lookup
//    did not produce a group.
//
// PURE, per this codebase's `insightsDateRange.ts` / `formatWorkZoneBreachLocation`
// precedent: devices, the group-member map, organization, and `getUsersGroup` are all
// parameters here, never a context read or a fetch performed inside this module. That is what
// makes this module testable as a plain function, without wrapping it in a provider.
//
// See the amended PRD D9/AR5 and design spec §5.2 for the full measured evidence and the
// attributed decision behind this two-source design.

import type {
  OrganizationOnAzure,
  MiniOrganization,
} from '@/../generated-client/models';
import type { OrgCompassDeviceListItem } from '@/models/OrgCompassDevice/OrgCompassDeviceListItem';

/**
 * Reads a group name for one `authId` from the organization's own roster document. Typed as
 * an injected function rather than imported directly: the real implementation is
 * `OrganizationProvider.getUsersGroup`, a context-bound function this module must not import
 * (that would be a context read inside a module that has to stay pure - see the header
 * comment). Returns `''` for "not found in any group" - `OrganizationProvider`'s own
 * convention (`OrganizationProvider.tsx`) - and callers of this resolver must NOT propagate
 * that empty string as if it were a real group name (PRD D8).
 */
export type GetUsersGroupFn = (
  organization: OrganizationOnAzure | MiniOrganization,
  authId: string,
) => string;

/**
 * The three-state resolution outcome for one breach's `authId` (PRD OQ1/D8):
 *
 *  - `no-user-recorded`: `authId === ''` - pre-promote history, or a sample that carried no
 *    identity. A pipeline-coverage fact, not a roster fact.
 *  - `unresolvable`: `authId` is a real value, but it was found in none of the sources. A
 *    roster/device fact (this id is absent from the device list and both group lookups), not
 *    a pipeline fact - collapsing this with `no-user-recorded` is exactly the ambiguity PRD
 *    OQ1's two distinct strings exist to keep apart.
 *  - `resolved`: `authId` was found in AT LEAST ONE source. `name`/`group` are each
 *    independently `string | null` - a resolved breach can legitimately have one half known
 *    and the other unknown (a real name with no group, or a real group with no name). NEVER
 *    collapse a partial resolution down to `unresolvable`: `unresolvable` means the authId
 *    itself was not found anywhere, not that one specific lookup came up empty for an authId
 *    that WAS found elsewhere.
 */
export type WorkZoneBreachUserResolution =
  | { kind: 'no-user-recorded' }
  | { kind: 'unresolvable' }
  | { kind: 'resolved'; name: string | null; group: string | null };

/**
 * Treats `null` and an empty/all-whitespace string identically - both are a MISS, never a
 * displayable name. `last_user_display_name` is nullable on the wire
 * (`OrgCompassDeviceListItem`); this is the exact falsy-value trap `InsightsTypes.ts`'s own
 * header convention already warns about elsewhere in this codebase (never coerce a
 * legitimately-absent value into something that renders as if it were real data) - re-
 * applied here to a nullable *string*, whereas that file's own examples are nullable
 * *numbers*.
 */
function normalizeDisplayName(rawName: string | null | undefined): string | null {
  if (rawName == null) return null;
  const trimmed = rawName.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export type BreachGroupMemberLookup = {
  name: string | null;
  groupName: string;
};

type GroupMemberSource = {
  group_name: string;
  group_value?: ReadonlyArray<{
    auth_id: string;
    first_name?: string | null;
    last_name?: string | null;
  }>;
};

function buildMemberDisplayName(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
): string | null {
  const nameParts = [normalizeDisplayName(firstName), normalizeDisplayName(lastName)]
    .filter((part): part is string => part !== null);
  return nameParts.length > 0 ? nameParts.join(' ') : null;
}

/**
 * Indexes the already-loaded `groups[].group_value` payload once so breach rows never scan
 * every group during render. First-write-wins when an auth id appears in multiple groups,
 * matching `buildBreachDeviceNameMap`'s deterministic duplicate rule.
 */
export function buildBreachGroupMemberMap(
  groups: ReadonlyArray<GroupMemberSource>,
): Map<string, BreachGroupMemberLookup> {
  const map = new Map<string, BreachGroupMemberLookup>();

  for (const group of groups) {
    for (const member of group.group_value ?? []) {
      if (!member.auth_id || map.has(member.auth_id)) continue;
      map.set(member.auth_id, {
        name: buildMemberDisplayName(member.first_name, member.last_name),
        groupName: group.group_name,
      });
    }
  }

  return map;
}

/**
 * Builds the `authId -> displayName` map from `getAllOrgCompassDevices()`'s response (the
 * NAME source - see this file's header comment). Keyed on `last_auth_id`; a device with no
 * last-known user (`last_auth_id === null`) contributes no entry, since there is no id to
 * key it by. First-write-wins on a duplicate `last_auth_id` (two devices whose last wearer
 * was the same person) - deterministic, and the values should agree in the ordinary case
 * since they describe the same person.
 *
 * A `has(authId)` on the returned map is still `true` even when the stored value is `null`
 * (a device attributes this `last_auth_id` but its own `last_user_display_name` came back
 * null/empty) - that distinction matters to `resolveWorkZoneBreachUser` below: it is what
 * lets "found in the device source, but no name available" stay a `resolved` (partial)
 * outcome rather than an `unresolvable` one.
 *
 * NEVER reads `OrganizationProvider`'s `groups` state - `devices` is the device list only.
 */
export function buildBreachDeviceNameMap(
  devices: OrgCompassDeviceListItem[],
): Map<string, string | null> {
  const map = new Map<string, string | null>();
  for (const device of devices) {
    if (!device.last_auth_id || map.has(device.last_auth_id)) continue;
    map.set(device.last_auth_id, normalizeDisplayName(device.last_user_display_name));
  }
  return map;
}

/**
 * Resolves one breach's `authId` against the injected sources. See `WorkZoneBreachUserResolution`'s
 * own doc comment above for the three states and the partial-resolution rule.
 *
 * `organization` is nullable because `Insights.tsx` tolerates a null organization elsewhere
 * on this page (see that file's own `UserDashboard` gating comment) - a null organization
 * simply means the group half cannot be looked up yet, which this function treats as "the
 * group source found nothing" rather than throwing.
 */
export function resolveWorkZoneBreachUser(
  authId: string,
  deviceNameMap: Map<string, string | null>,
  organization: OrganizationOnAzure | MiniOrganization | null,
  getUsersGroup: GetUsersGroupFn,
  groupMemberMap: Map<string, BreachGroupMemberLookup> = new Map(),
): WorkZoneBreachUserResolution {
  if (authId === '') {
    return { kind: 'no-user-recorded' };
  }

  const foundInDeviceList = deviceNameMap.has(authId);

  // '' is getUsersGroup's own "not found" sentinel (OrganizationProvider.tsx) - translated
  // to `null` here, EXPLICITLY, rather than passed through as if it were a real group name.
  // This is the exact falsy-value trap InsightsTypes.ts's own header convention warns about:
  // an empty string is a miss, not a value, and must never reach a rendered cell as one.
  const rawGroup = organization ? getUsersGroup(organization, authId) : '';
  const groupMember = groupMemberMap.get(authId);
  const group = rawGroup === '' ? groupMember?.groupName ?? null : rawGroup;
  const foundInGroupSource = group !== null;
  const foundInGroupMemberSource = groupMember !== undefined;

  if (!foundInDeviceList && !foundInGroupSource && !foundInGroupMemberSource) {
    return { kind: 'unresolvable' };
  }

  return {
    kind: 'resolved',
    name:
      (foundInDeviceList ? deviceNameMap.get(authId) ?? null : null) ??
      groupMember?.name ??
      null,
    group,
  };
}
